import { BLSProvider } from './context/BLSContext';
import { UIModeProvider } from './context/UIModeContext';
import { FreemiumProvider } from './context/FreemiumContext';
import { RoleProvider, useRole } from './context/RoleContext';
import { useZoomApp } from './hooks/useZoomApp';
import { useAuth } from './hooks/useAuth';
import { TherapistPanel } from './components/TherapistPanel';
import { ClientView } from './components/ClientView';
import { TherapistOnboarding } from './components/onboarding/TherapistOnboarding';
import { NotInMeetingPage } from './components/NotInMeetingPage';
import { Auth } from './pages/Auth';
import { PricingPage } from './pages/PricingPage';
import { CheckoutReturnHandler } from './components/CheckoutReturnHandler';
import { LegalAgreementsForm } from './components/onboarding/LegalAgreementsForm';
import './styles/onboarding.css';

/**
 * App - Main application component
 *
 * ROUTING LOGIC:
 * - Auto-therapist: Hosts are automatically set as therapists
 * - Auto-patient: Participants are automatically set as patients
 * - Auth-first: Always verify Supabase session for therapists
 * - Direct access: Patients go directly to ClientView
 * - /auth: OAuth callback page
 * - /pricing: Standalone pricing page
 * - /preview-agreements: Preview legal agreements form (testing)
 */

function AppContent(): JSX.Element {
  // Handle auth callback route
  if (window.location.pathname === '/auth') {
    return <Auth />;
  }

  // Handle pricing page route
  if (window.location.pathname === '/pricing') {
    return <PricingPage />;
  }

  // Handle preview route for legal agreements (dev/testing only)
  if (window.location.pathname === '/preview-agreements') {
    return <LegalAgreementsForm onComplete={() => console.log('Preview: Agreements accepted')} />;
  }

  const { userRole: zoomRole, isInitializing, error } = useZoomApp();
  const { authState } = useAuth();
  const { role: appRole, setRole, hasAcceptedAgreements } = useRole();

  // Loading state - Zoom SDK initializing
  if (isInitializing) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-teal-50 to-blue-50">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-teal-200 border-t-teal-600 mx-auto"></div>
          <p className="text-gray-600 font-medium">Initializing EMDR App...</p>
        </div>
      </div>
    );
  }

  // Error state - Zoom SDK failed (app accessed outside of a meeting)
  // Show friendly "not in meeting" page instead of error
  if (error) {
    const urlParams = new URLSearchParams(window.location.search);
    const isInstallSuccess = urlParams.get('install') === 'success';
    return <NotInMeetingPage isInstallSuccess={isInstallSuccess} />;
  }

  // ====================================================================
  // AUTOMATIC ROLE ASSIGNMENT
  // ====================================================================

  // Auto-set patient for non-hosts (participants)
  if (!appRole && zoomRole === 'participant') {
    console.log('[App] Auto-setting role to patient (non-owner)');
    setRole('patient');
    // Will re-render with appRole = 'patient'
    return <></>;  // Prevent flash
  }

  // Auto-set therapist for hosts with no role
  if (!appRole && zoomRole === 'host') {
    console.log('[App] Auto-setting role to therapist (host)');
    setRole('therapist');
    // Will re-render with appRole = 'therapist'
    return <></>;  // Prevent flash
  }

  // ====================================================================
  // THERAPIST FLOW - Auth-First Logic
  // ====================================================================
  if (appRole === 'therapist') {
    // CRITICAL: Verify active Supabase session exists
    // Do NOT trust localStorage alone
    if (authState !== 'signed-in') {
      console.log('[App] Therapist role but not signed in - showing onboarding');
      return <TherapistOnboarding />;
    }

    // Check if agreements are needed
    // Check localStorage (RoleContext) for quick check to avoid showing form unnecessarily
    // If localStorage says they haven't accepted, TherapistOnboarding will verify against DB
    if (!hasAcceptedAgreements) {
      console.log('[App] Therapist authenticated but needs to accept agreements');
      return <TherapistOnboarding />;
    }

    console.log('[App] Therapist authenticated - showing panel');
    return <TherapistPanel />;
  }

  // ====================================================================
  // PATIENT FLOW - Direct Access (No Disclaimer)
  // ====================================================================
  if (appRole === 'patient') {
    console.log('[App] Patient role - showing ClientView (no disclaimer)');
    return <ClientView />;
  }

  // Fallback - should not reach here
  console.warn('[App] Unexpected state - showing therapist onboarding');
  return <TherapistOnboarding />;
}

export function App(): JSX.Element {
  return (
    <RoleProvider>
      <FreemiumProvider>
        <BLSProvider>
          <UIModeProvider>
            <CheckoutReturnHandler />
            <AppContent />
          </UIModeProvider>
        </BLSProvider>
      </FreemiumProvider>
    </RoleProvider>
  );
}
